import { cn } from "@/utils/helpers"
import { cva, VariantProps } from "class-variance-authority"
import * as React from "react"
import { Icon } from "./icon/Icon"
import { TIconName } from "./icon/IconUtil"

const infoBoxVariants = cva("border flex flex-col gap-2", {
  variants: {
    variant: {
      neutral: "bg-container-base border-outline text-on-surface",
      success:
        "bg-success-container border-on-success-container text-on-success-container",
      warning:
        "bg-warning-container border-on-warning-container text-on-warning-container",
      information:
        "bg-primary-container border-primary text-on-primary-container",
      error: "bg-error-container border-error text-on-error-container",
    },
    size: {
      sm: "px-3 py-3 text-body-sm",
      md: "px-4 py-3 text-body-base",
      lg: "px-6 py-6 text-body-lg items-start",
    },
    hasOutline: {
      true: "border",
      false: "border-0",
    },
    hasRoundedCorners: {
      true: "rounded-lg",
      false: "rounded-none",
    },
  },
  defaultVariants: {
    variant: "neutral",
    size: "md",
    hasOutline: false,
    hasRoundedCorners: true,
  },
})

export interface InfoBoxProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof infoBoxVariants> {
  icon?: boolean
  extendedIcon?: {
    name: TIconName
    fill?: boolean
  }
  action?: React.ReactNode
  children: React.ReactNode
}

const InfoBox = React.forwardRef<HTMLDivElement, InfoBoxProps>(
  (
    {
      className,
      variant,
      size,
      icon = true,
      hasOutline,
      extendedIcon,
      action,
      hasRoundedCorners,
      ...props
    },
    ref,
  ) => {
    const iconName: TIconName = (() => {
      switch (variant) {
        case "error":
          return "dangerous"
        case "information":
          return "info"
        case "neutral":
          return "info"
        case "success":
          return "check_circle"
        case "warning":
          return "warning"
      }
    })()
    return (
      <div
        ref={ref}
        className={cn(
          infoBoxVariants({ variant, size, hasOutline, hasRoundedCorners }),
          className,
        )}
        {...props}
      >
        <div className="flex flex-row gap-2">
          {icon && (
            <Icon
              icon={extendedIcon ? extendedIcon.name : iconName}
              fill={
                extendedIcon
                  ? (extendedIcon.fill ?? variant !== "neutral")
                  : variant !== "neutral"
              }
              size={size === "sm" ? "1.25rem" : "1.5rem"}
              className={cn({
                "text-error": variant === "error",
                "text-on-surface-variant": variant === "neutral",
              })}
            />
          )}
          {props.children}
        </div>
        {action}
      </div>
    )
  },
)
InfoBox.displayName = "InfoBox"

export { InfoBox }
